<template>
    <div class="container-fluid">
        <FormMAInfo v-model="maInfo" :readonly="isViewMode" />
        <FormChecklistProduct v-model="checklistData" :readonly="isViewMode"/>
        <FormMASignature v-model="signatureData" :readonly="isViewMode" @register-qr-code="registerQRCode"/>
        <div class="col-xl-12">
            <div class="d-flex gap-2 pull-right mb-4">
                <button class="btn btn-success pull-right" type="button" @click="submitForm"><span v-if="isSubmitting">กำลังบันทึก...</span><span v-else>บันทึก</span></button>
                <button class="btn btn-danger pull-right" type="button" @click="deleteForm"><span v-if="isDeleting">กำลังลบ...</span><span v-else>ลบ</span></button>
            </div>
        </div>
    </div>
</template>
<script setup>

import { useRoute , useRouter } from 'vue-router';
import { ref, computed, onMounted , defineAsyncComponent , watch } from 'vue'
import { directus } from "@/services/directus"
import { readItems , updateItem , deleteItem} from "@directus/sdk"
import Swal from 'sweetalert2'
import axios from 'axios';
const directus_ip = process.env.VUE_APP_DIRECTUS_URL && process.env.VUE_APP_DIRECTUS_URL.startsWith("http")
    ? process.env.VUE_APP_DIRECTUS_URL
    : 'http://localhost:8056';

async function registerQRCode() {
  if (isRegisteringQR.value) return;
  isRegisteringQR.value = true;
  try {
    const payload = {
        id: route.params.id,
        company_name: maInfo.value.company_name,
        store_name: maInfo.value.store_name,
        branch_name: maInfo.value.branch_name,
        branch_code: maInfo.value.branch_code,
        have_pos_1: checklistData.value.have_pos_1,
        check_list_pos_1: checklistData.value.check_list_pos_1,
        have_pos_2: checklistData.value.have_pos_2,
        check_list_pos_2: checklistData.value.check_list_pos_2,
        have_kitchen_printer: checklistData.value.have_kitchen_printer,
        check_list_kitchen_printer: checklistData.value.check_list_kitchen_printer,
        have_receipt_printer: checklistData.value.have_receipt_printer,
        check_list_receipt_printer: checklistData.value.check_list_receipt_printer,
        have_label_printer: checklistData.value.have_label_printer,
        check_list_label_printer: checklistData.value.check_list_label_printer,
        have_drawer: checklistData.value.have_drawer,
        check_list_drawer: checklistData.value.check_list_drawer,
        have_scanner: checklistData.value.have_scanner,
        check_list_scanner: checklistData.value.check_list_scanner,
        have_ups: checklistData.value.have_ups,
        check_list_ups: checklistData.value.check_list_ups,
        have_kiosk: checklistData.value.have_kiosk,
        check_list_kiosk: checklistData.value.check_list_kiosk,
        have_printer_queue: checklistData.value.have_printer_queue,
        check_list_printer_queue: checklistData.value.check_list_printer_queue,
    };
    const response = await axios.post(`${directus_ip}/register-qr-code-api/register`, payload);
    customAlert('ลงทะเบียน QR Code สำเร็จ', 'success');
  } catch (error) {
    customAlert('เกิดข้อผิดพลาดในการลงทะเบียน QR Code', 'error');
  } finally {
    isRegisteringQR.value = false;
  }
}
const isRegisteringQR = ref(false);
const FormMAInfo = defineAsyncComponent(() => import("@/components/theme/ma/FormMAInfo.vue"));
const FormChecklistProduct = defineAsyncComponent(() => import("@/components/theme/ma/FormChecklistProduct.vue"));
const FormMASignature = defineAsyncComponent(() => import("@/components/theme/ma/FormMASignature.vue"));

const route = useRoute()
const router = useRouter()
const maInfo = ref({})
const checklistData = ref([])
const signatureData = ref(null)
const isSubmitting = ref(false);
const isDeleting = ref(false);

const isViewMode = computed(() => !!route.params.id)
const maId = route.params.id

onMounted(() => {
  if (isViewMode.value) {
    fetchMADetail(maId)
  }
})

function getChecklistStatus(maInfoVal, checklistVal, signatureVal) {
    const keys = Object.keys(checklistVal);
    const haveKeys = keys.filter(k => k.startsWith('have_'));

    let hasPendingCheck = false;
    let hasBroken = false;
    let hasInfo = false;
    let missingImage = false;

    if (maInfoVal.company_name === null) hasInfo = true;
    if (maInfoVal.store_name === null) hasInfo = true;
    if (maInfoVal.branch_name === null) hasInfo = true;
    if (maInfoVal.inspection_date === null) hasInfo = true;

    for (const haveKey of haveKeys) {
        if (!checklistVal[haveKey]) continue;

        const suffix = haveKey.replace('have_', '');
        const checklistKey = `check_list_${suffix}`;
        const imageKey = `image_${suffix}`;
        const checklists = checklistVal[checklistKey] || [];
        const images = checklistVal[imageKey] || [];

        // ตรวจสอบ checklist status
        for (const checklistGroup of checklists) {
            for (const group of checklistGroup.checklist) {
                for (const item of group.items) {
                    const status = item.status;
                    const serial_number = checklistGroup.serial_number;
                    const product_name = checklistGroup.product_name;
                    const qr_code = checklistGroup.qr_code;

                    if (status === 'abnormal') hasBroken = true;
                    if (product_name === '') hasInfo = true;
                    if (serial_number === '') hasInfo = true;
                    if (qr_code === '') hasInfo = true;
                    if (status === null || status === undefined) hasPendingCheck = true;
                }
            }
        }

        // ตรวจสอบรูปภาพ
        const hasValidImage = images.some(img => img.directus_files_id != null);
        if (!hasValidImage) {
            missingImage = true;
            break;
        }
    }
    const hasImageUploaded = !missingImage;

    const documents = (signatureVal && signatureVal['ma_documents']) || [];
    let hasDocumentUploaded = false;
    for (const doc of documents) {
        if (doc.directus_files_id !== null && doc.directus_files_id !== undefined) {
            hasDocumentUploaded = true;
            break;
        }
    }

    // สรุปผลลัพธ์ตามลำดับความสำคัญ
    if (hasInfo) return 'no_info';
    if (hasBroken) return 'broken';
    if (hasPendingCheck) return 'pending_check';
    if (!hasImageUploaded) return 'upload_image';
    if (!hasDocumentUploaded) return 'upload_document';
    return 'passed';
}

const status = computed(() => {
  return getChecklistStatus(maInfo.value, checklistData.value, signatureData.value);
});

watch(status, (newStatus) => {
  signatureData.value = {
    ...signatureData.value,
    status: newStatus
  };
  updateForm();
});

watch(
  [() => signatureData.value],
  () => {
    updateForm();
  },
  { deep: true }
);

async function fetchMADetail(id) {
    const response = await directus.request(
        readItems("ma_list", {
            filter: {
                id: { _eq: id },
            },
            fields: ["*"],
            limit: -1,
        })
    );
    const dummyData = response[0]
    maInfo.value = {
        company_name: dummyData.company_name,
        store_name: dummyData.store_name,
        branch_name: dummyData.branch_name,
        branch_code: dummyData.branch_code,
        inspection_date: dummyData.inspection_date,
    };
    checklistData.value = {
        have_pos_1: dummyData.have_pos_1,
        note_pos_1: dummyData.note_pos_1,
        check_list_pos_1: dummyData.check_list_pos_1,
        have_pos_2: dummyData.have_pos_2,
        note_pos_2: dummyData.note_pos_2,
        check_list_pos_2: dummyData.check_list_pos_2,
        have_kitchen_printer: dummyData.have_kitchen_printer,
        note_kitchen_printer: dummyData.note_kitchen_printer,
        check_list_kitchen_printer: dummyData.check_list_kitchen_printer,
        have_receipt_printer: dummyData.have_receipt_printer,
        note_receipt_printer: dummyData.note_receipt_printer,
        check_list_receipt_printer: dummyData.check_list_receipt_printer,
        have_label_printer: dummyData.have_label_printer,
        note_label_printer: dummyData.note_label_printer,
        check_list_label_printer: dummyData.check_list_label_printer,
        have_drawer: dummyData.have_drawer,
        note_drawer: dummyData.note_drawer,
        check_list_drawer: dummyData.check_list_drawer,
        have_scanner: dummyData.have_scanner,
        note_scanner: dummyData.note_scanner,
        check_list_scanner: dummyData.check_list_scanner,
        have_ups: dummyData.have_ups,
        note_ups: dummyData.note_ups,
        check_list_ups: dummyData.check_list_ups,
        have_kiosk: dummyData.have_kiosk,
        note_kiosk: dummyData.note_kiosk,
        check_list_kiosk: dummyData.check_list_kiosk,
        have_printer_queue: dummyData.have_printer_queue,
        note_printer_queue: dummyData.note_printer_queue,
        check_list_printer_queue: dummyData.check_list_printer_queue,
    };
    signatureData.value = {
        status: dummyData.status,
        job_position: dummyData.job_position,
        isNonBangkok: dummyData.isNonBangkok
    }
}

async function submitForm() {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    await directus.request(updateItem("ma_list", maId, {
        company_name: maInfo.value.company_name,
        store_name: maInfo.value.store_name,
        branch_name: maInfo.value.branch_name,
        branch_code: maInfo.value.branch_code,
        inspection_date: maInfo.value.inspection_date,
        have_pos_1: checklistData.value.have_pos_1,
        note_pos_1: checklistData.value.note_pos_1,
        check_list_pos_1: checklistData.value.check_list_pos_1,
        have_pos_2: checklistData.value.have_pos_2,
        note_pos_2: checklistData.value.note_pos_2,
        check_list_pos_2: checklistData.value.check_list_pos_2,
        have_kitchen_printer: checklistData.value.have_kitchen_printer,
        note_kitchen_printer: checklistData.value.note_kitchen_printer,
        check_list_kitchen_printer: checklistData.value.check_list_kitchen_printer,
        have_receipt_printer: checklistData.value.have_receipt_printer,
        note_receipt_printer: checklistData.value.note_receipt_printer,
        check_list_receipt_printer: checklistData.value.check_list_receipt_printer,
        have_label_printer: checklistData.value.have_label_printer,
        note_label_printer: checklistData.value.note_label_printer,
        check_list_label_printer: checklistData.value.check_list_label_printer,
        have_drawer: checklistData.value.have_drawer,
        note_drawer: checklistData.value.note_drawer,
        check_list_drawer: checklistData.value.check_list_drawer,
        have_scanner: checklistData.value.have_scanner,
        note_scanner: checklistData.value.note_scanner,
        check_list_scanner: checklistData.value.check_list_scanner,
        have_ups: checklistData.value.have_ups,
        note_ups: checklistData.value.note_ups,
        check_list_ups: checklistData.value.check_list_ups,
        have_kiosk: checklistData.value.have_kiosk,
        note_kiosk: checklistData.value.note_kiosk,
        check_list_kiosk: checklistData.value.check_list_kiosk,
        have_printer_queue: checklistData.value.have_printer_queue,
        note_printer_queue: checklistData.value.note_printer_queue,
        check_list_printer_queue: checklistData.value.check_list_printer_queue,
        status: signatureData.value.status,
        isNonBangkok: signatureData.value.isNonBangkok,
        job_position: signatureData.value.job_position,
    }));
    isSubmitting.value = false;
    customAlert("บันทึกสำเร็จ", "success")
}

async function updateForm() {
    await directus.request(updateItem("ma_list", maId, {
        company_name: maInfo.value.company_name,
        store_name: maInfo.value.store_name,
        branch_name: maInfo.value.branch_name,
        branch_code: maInfo.value.branch_code,
        inspection_date: maInfo.value.inspection_date,
        have_pos_1: checklistData.value.have_pos_1,
        note_pos_1: checklistData.value.note_pos_1,
        check_list_pos_1: checklistData.value.check_list_pos_1,
        have_pos_2: checklistData.value.have_pos_2,
        note_pos_2: checklistData.value.note_pos_2,
        check_list_pos_2: checklistData.value.check_list_pos_2,
        have_kitchen_printer: checklistData.value.have_kitchen_printer,
        note_kitchen_printer: checklistData.value.note_kitchen_printer,
        check_list_kitchen_printer: checklistData.value.check_list_kitchen_printer,
        have_receipt_printer: checklistData.value.have_receipt_printer,
        note_receipt_printer: checklistData.value.note_receipt_printer,
        check_list_receipt_printer: checklistData.value.check_list_receipt_printer,
        have_label_printer: checklistData.value.have_label_printer,
        note_label_printer: checklistData.value.note_label_printer,
        check_list_label_printer: checklistData.value.check_list_label_printer,
        have_drawer: checklistData.value.have_drawer,
        note_drawer: checklistData.value.note_drawer,
        check_list_drawer: checklistData.value.check_list_drawer,
        have_scanner: checklistData.value.have_scanner,
        note_scanner: checklistData.value.note_scanner,
        check_list_scanner: checklistData.value.check_list_scanner,
        have_ups: checklistData.value.have_ups,
        note_ups: checklistData.value.note_ups,
        check_list_ups: checklistData.value.check_list_ups,
        have_kiosk: checklistData.value.have_kiosk,
        note_kiosk: checklistData.value.note_kiosk,
        check_list_kiosk: checklistData.value.check_list_kiosk,
        have_printer_queue: checklistData.value.have_printer_queue,
        note_printer_queue: checklistData.value.note_printer_queue,
        check_list_printer_queue: checklistData.value.check_list_printer_queue,
        status: signatureData.value.status,
        isNonBangkok: signatureData.value.isNonBangkok,
        job_position: signatureData.value.job_position,
    }));
}

async function deleteForm() {
    const confirmDeleteResult = await questionsAlert()
    if (!confirmDeleteResult.isConfirmed) return;

    if (isDeleting.value) return;
    isDeleting.value = true;
    await directus.request(deleteItem("ma_list", maId));
    isDeleting.value = false;
    customAlert("ลบสำเร็จ", "success")
    router.push({ name: 'listMA' });
}

function customAlert(txt,type) {
    Swal.fire({
        icon: type,
        title: txt,
        confirmButtonColor: 'var(--theme-default)',
    });
}

function questionsAlert() {
    return Swal.fire({
        icon: "warning",
        text: 'ยืนยันการลบข้อมูล?',
        showCancelButton: true,
        cancelButtonText: "ยกเลิก",
        cancelButtonColor: '#efefef',
        confirmButtonText: "ใช่, ลบเลย!",
        confirmButtonColor: 'var(--theme-default)',
    });
}
</script>